import React, { useEffect } from "react";
import { Button } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SyncIcon from "@mui/icons-material/Sync";
import SearchV2 from "./SearchV2";
import excelIcon from "../../assets/images/png/reportExcel.png";
import "./HeaderV2.css";
import { Dayjs } from "dayjs";
import { StatusFilterMenuItems } from "../../types/types";
import StatusFilter from "../../features/deliveryConfirmation/StatusFilter";
import RealTimeStockStatusFilter from "../../features/stocks/RealTimeStock/RealTimeStockStatusFilter";
import CachedIcon from '@mui/icons-material/Cached';
import { ReactComponent as ArrowsCounterClockwise } from "../../assets/image/svg/ArrowsCounterClockwise.svg";
import QuickDateRangePicker from "./QuickDateRangePicker";
import TextSwitcher, { TextSwitcherItemObject } from "./TextSwitcher";

interface HeaderV2Props<T> {
  title: string;
  searchFunction?: (value: string) => void;
  onRefresh?: () => void;
  handleCreateBtnClick?: () => void;
  createBtnLabel?: string;
  downloadReportIcon?: boolean;
  downloadReportOnClick?:()=>void;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  lastRefreshed?:Dayjs;
  resetSearch?: boolean;
  showDistributorFilter?: boolean;
  onDistributorChange?: (distributorId: string) => void;
  distributorList?: StatusFilterMenuItems[];
  enableTruncation?: boolean;
  setStartDate?: React.Dispatch<React.SetStateAction<Dayjs>>;
  setEndDate?: React.Dispatch<React.SetStateAction<Dayjs>>;
  startDate?: Dayjs;
  endDate?: Dayjs;
  textSwitcherItems?: TextSwitcherItemObject[];
  showDistributeButton?: boolean;
  onDistributeClick?: () => void;
  distributeButtonLabel?: string;
  showNoteBanner?: boolean;
  setDateRangeLabel?: React.Dispatch<React.SetStateAction<string>>;
  handleCheckListClick?: () => void;
}

const HeaderV2 = <T extends Record<string, any>>({
  title,
  searchFunction,
  onRefresh,
  handleCreateBtnClick,
  createBtnLabel = "",
  downloadReportIcon = false,
  downloadReportOnClick,
  buttonRef,
  lastRefreshed,
  resetSearch = false,
  showDistributorFilter = false,
  onDistributorChange,
  distributorList = [],
  enableTruncation = false,
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  textSwitcherItems = [],
  showDistributeButton = false,
  onDistributeClick,
  distributeButtonLabel = "Distribute SKUs equally",
  showNoteBanner = false,
  setDateRangeLabel,
  handleCheckListClick,
}: HeaderV2Props<T>) => {

useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.shiftKey && event.key === 'N') {
      event.preventDefault();
      const activeElement = document.activeElement;
      const isInputField =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.tagName === 'SELECT';

      if (!isInputField && handleCreateBtnClick) {
        handleCreateBtnClick();
      }
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [handleCreateBtnClick, title, createBtnLabel, searchFunction, showDistributorFilter]);

  const handleStartDateChange = ([newStartDate, newEndDate]: [Dayjs, Dayjs]) => {
    setStartDate?.(newStartDate);
    setEndDate?.(newEndDate);
    console.log("Selected Date Range:", newStartDate.format("YYYY-MM-DD"), "to", newEndDate.format("YYYY-MM-DD"));
  };

  return (
    <div className="HeaderV2">
      <div className="HeaderV2_label" style={{ flex: 1, justifyContent: 'flex-start' }}>
        <div className="HeaderV2_title">
          <div className="HeaderV2_label_bar" />
          {title}
        </div>
        { (showNoteBanner) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '20px',
            padding: '4px 12px',
            background: '#FFF7EE',
            marginTop: '8px',
            marginBottom: '8px',
            width: 'fit-content',
            fontSize: '13px'
          }}
        >
          <span style={{ fontWeight: 400, color: '#252525', marginRight: 4 }}>Please note:</span>
          <span style={{ color: '#9C5B12', fontWeight: 400, marginRight: 12 }}>
          Remaining qty will auto-replace with the mapped SKU in the product replacement
          </span>
        </div>
)}
        {lastRefreshed && (
          <div className="HeaderV2_lastRefreshed" style={{ display: 'inline-flex', alignItems: 'center' , gap: "6px"}}>
            {onRefresh && lastRefreshed && (
              <ArrowsCounterClockwise
                className="HeaderV2_refresh"
                onClick={onRefresh}
              />
            )}
            Last updated on: {lastRefreshed.format("YYYY-MM-DD HH:mm:ss")}
          </div>
        )}
      

      </div>
      <div
        className={`HeaderV2_tools_container ${handleCreateBtnClick ? "" : "searchJustifyEnd"}`}
      >
        {showDistributorFilter && distributorList.length > 0 && onDistributorChange && (
          <div className="HeaderV2_distributor_filter" style={{ marginRight: '15px' }}>
            <RealTimeStockStatusFilter
              onStatusChange={onDistributorChange}
              statusFilterMenuItems={distributorList}
              enableTruncation={enableTruncation}
            />
          </div>
        )}
        
        {onRefresh && !lastRefreshed &&(
          <SyncIcon
            className="HeaderV2_refresh"
            sx={{ fill: "#1C7EA0 !important" }}
            onClick={onRefresh}
          />
        )}
        {setStartDate && (
          <QuickDateRangePicker handleStartDateChange={handleStartDateChange} startDate={startDate} endDate={endDate} setDateRangeLabel={setDateRangeLabel}/>
        )}
        {showDistributeButton && onDistributeClick && (
          <Button 
            variant="contained"
            size="small"
            onClick={onDistributeClick}
            sx={{ 
              textTransform: 'none',
              color: '#227C9D',
              bgcolor: '#F0FBFF',
              fontSize: '12px',
              marginRight: '16px',
              '&:hover, &:focus, &:active': {
                bgcolor: '#F0FBFF',
              },
              border: '1px solid #227C9D',
              borderRadius: '10px',
              boxShadow: 'none',
            }}
          >
            {distributeButtonLabel}
          </Button>
        )}
        {searchFunction && (
          <div className={`${handleCreateBtnClick ? "" : "searchWidth"}`}>
            <SearchV2 searchFunction={searchFunction} resetSearch={resetSearch} />
          </div>
        )}
        {handleCreateBtnClick && (
          <Button
            ref={buttonRef}
            variant="contained"
            sx={{
              backgroundColor: "#237C9D",
              padding: "0.5rem 0.5rem",
              fontSize: "0.6rem",
              minWidth: "11rem",
              height: "40px",
              boxShadow:'none'
            }}
            onClick={handleCreateBtnClick}
          >
            <AddCircleIcon sx={{ fontSize: "18px", marginRight: "5px" }} />{" "}
            <span className="HeaderV2_createBtn_text">{createBtnLabel}</span>
            &nbsp;<span style={{fontSize:"9.5px", textTransform:"none"}}>(Shift + N)</span>
          </Button>
        )}
        {downloadReportIcon && (
          <img src={excelIcon} alt="Report" className="excelIcon" onClick={downloadReportOnClick}/>
        )}
      </div>
    </div>
  );
};

export default HeaderV2;
